import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { ILesson } from '@dtos/Course'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useMediaSize } from '@hooks/useMediaSize'
import { useScaleAnimation } from '@hooks/useScaleAnimation'
import { useThemeColor } from '@hooks/useThemeColor'
import { useDomain, useHasFeature } from '@hooks/useThemeParams'
import { isContentAvailable } from '@utils/contentAvailable'
import { formatDateShort } from '@utils/dateFormatter'
import {
  productRedirectToEnrollment,
  REDIRECT_TO_CONTENT_TYPES,
} from '@utils/productRedirectToEnrollment'
import { LinearGradient } from 'expo-linear-gradient'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated } from 'react-native'
import { FontSizeTokens, isWeb, useMedia, View, ViewProps } from 'tamagui'
import { CoverImage } from '@components/CoverImage'
import { ProductCardContent } from '../ProductCardContent'
import { ProductBadge } from './ProductBadge'
import { ProductProgress } from './ProductProgress'

interface IProductCardProps {
  data: {
    enrollmentId?: string
    id: string
    courseId?: string | null
    availableUntil?: string
    activatedAt?: string
    progress?: number
    completedLessons?: number
    totalLessons?: number
    title?: string
    logo?: string
    logoVertical?: string
    kind?: string | undefined | null
    hasLiveSoon?: boolean
    pathId?: number | null
    courseContentId?: number | null
    firstLesson?: ILesson | null
  }
  hideProgressInformation?: boolean
  isHorizontal?: boolean
  hasAuxiliarTitle?: boolean
  displayBuyTag?: boolean
  /** Selo "Bloqueado" sem data: produto que o aluno não tem e que o app não
   * vende (app da loja, ver `@constants/storeRules`). */
  displayLockTag?: boolean
  customOnPress?: () => Promise<void>
  /** Disponibilidade decidida por quem usa o card (ex.: módulo na home
   * focada). Sem a prop, vale a regra por datas da matrícula. */
  isAvailable?: boolean
  /** Capa usada quando o item não tem `logo`/`logoVertical`. */
  fallbackCover?: string
  accessibilityLabel?: ViewProps['accessibilityLabel']
  accessibilityRole?: ViewProps['accessibilityRole']
}

export function ProductCard({
  isHorizontal = true,
  data,
  hasAuxiliarTitle,
  displayBuyTag,
  displayLockTag = false,
  customOnPress,
  hideProgressInformation = false,
  isAvailable,
  fallbackCover,
  ...extraProps
}: IProductCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { t } = useTranslation('Home')
  const media = useMedia()
  const cardHoverShadow = useThemeColor('bg-surface-raised', 0.24)
  const { setCurrentLesson } = useCurrentLesson()
  const { subdomainURL } = useDomain()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const { isPreview } = useAdminPreview()
  const animatedOpacity = useRef(new Animated.Value(0)).current
  const interpolatedOpacity = animatedOpacity.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0],
  })
  const size = useMediaSize()
  const { startAnimation, endAnimation, scaleValues } = useScaleAnimation(
    data.enrollmentId || data.id,
  )

  const cardDimensions = useMemo(() => {
    const orientation = isHorizontal ? 'horizontal' : 'vertical'
    return CARD_DIMENSIONS[orientation][size] || CARD_DIMENSIONS[orientation].xs
  }, [isHorizontal, size])

  const isAvailableByDates = data.activatedAt
    ? isContentAvailable({
        createdAt: data.activatedAt,
        releaseAfter: null,
        releaseAt: null,
        lockAfter: null,
        lockAt: null,
        availableUntil: data.availableUntil,
      })
    : true
  const isProductAvailable = isAvailable ?? isAvailableByDates
  const isLiveProduct = data.kind === 'live_meet'
  const shouldShowIndicatorOnHover =
    isProductAvailable && !hasAuxiliarTitle && media.gtSm
  // O selo de bloqueio mostra a data de fim do acesso; sem ela (bloqueio
  // vindo de `isAvailable`), o estado fica no rodapé "Bloqueado".
  const shouldRenderLockBadge =
    !isProductAvailable && !displayBuyTag && !!data.availableUntil
  const shouldRenderLiveBadge =
    isProductAvailable &&
    isLiveProduct &&
    !displayBuyTag &&
    !displayLockTag &&
    data.hasLiveSoon
  const shouldRenderProgress =
    media.md &&
    !hasAuxiliarTitle &&
    isProductAvailable &&
    !hideProgressInformation
  const cardImageBorderRadius = hasAuxiliarTitle ? 0 : 8
  const displayProgressOnHover =
    media.gtSm && isHovered && !hideProgressInformation

  const showIndicatorOnHover = useCallback(() => {
    if (!shouldShowIndicatorOnHover) return
    setIsHovered(true)
  }, [shouldShowIndicatorOnHover])

  const isCourseIdValid = data.courseId || data.pathId

  const handleCardPress = useCallback(async () => {
    if (isAvailable === false || displayLockTag) return
    if (customOnPress) {
      await customOnPress()
      return
    }
    if (!isCourseIdValid || !isProductAvailable) return

    if (
      data.kind &&
      REDIRECT_TO_CONTENT_TYPES.includes(data.kind) &&
      data.firstLesson
    ) {
      setCurrentLesson({
        ...data.firstLesson,
        media: data.firstLesson.media ?? null,
      })
    }

    productRedirectToEnrollment({
      enrollmentId: data.enrollmentId || '',
      courseId: data.courseId,
      courseContentId: data.courseContentId,
      pathId: data.pathId,
      kind: data.kind,
      baseUrl: subdomainURL,
      hasCourseContentsPage,
      isPreview,
    })
  }, [
    customOnPress,
    isAvailable,
    displayLockTag,
    isCourseIdValid,
    isProductAvailable,
    data.kind,
    data.firstLesson,
    data.enrollmentId,
    data.courseId,
    data.courseContentId,
    data.pathId,
    setCurrentLesson,
    subdomainURL,
    hasCourseContentsPage,
    isPreview,
  ])

  useEffect(() => {
    if (!shouldRenderLiveBadge) return

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedOpacity, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
        Animated.timing(animatedOpacity, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: false,
        }),
      ]),
    )
    animation.start()
    return () => animation.stop()
  }, [animatedOpacity, shouldRenderLiveBadge])

  const isCompleted = data.progress === 100
  const getProgress = isProductAvailable && data.progress ? data.progress : 0
  const getProgressIndicatorColor = isCompleted
    ? '$progress-complete'
    : '$progress-fill'
  const isStarted = (data.progress ?? 0) > 0
  const lessonsQuantityFontSize: FontSizeTokens = media.gtMd
    ? '$font-size-xxs'
    : '$font-size-xxxs'
  const progressBarHeight = media.gtSm ? 8 : 6
  const isNativeAndNotAvailable = !isProductAvailable && !isWeb
  const productLogo =
    (!isHorizontal && data.logoVertical ? data.logoVertical : data.logo) ||
    fallbackCover

  const badgeIconSize = useMemo(() => {
    if (media.gtLg) return 20
    if (media.gtSm) return 16
    return 12
  }, [media.gtLg, media.gtSm])

  const cardWidth = isWeb
    ? `${cardDimensions.width}px !important`
    : cardDimensions.width

  const handleMouseEnter = useCallback(() => {
    if (isProductAvailable) startAnimation(data.enrollmentId || data.id)
  }, [isProductAvailable, startAnimation, data.enrollmentId, data.id])

  const handleMouseLeave = useCallback(() => {
    if (isProductAvailable) endAnimation(data.enrollmentId || data.id)
  }, [isProductAvailable, endAnimation, data.enrollmentId, data.id])

  const handleHoverOut = useCallback(() => {
    setIsHovered(false)
  }, [])

  const progressProps = {
    progress: getProgress,
    completedLessons: data.completedLessons,
    totalLessons: data.totalLessons,
    progressBarHeight,
    progressIndicatorColor: getProgressIndicatorColor,
    isStarted,
    isCompleted,
    lessonsQuantityFontSize,
  }

  return (
    <View
      {...extraProps}
      maxWidth={cardWidth}
      minWidth={cardWidth}
      onPress={handleCardPress}
      {...(displayLockTag && { accessibilityState: { disabled: true } })}
      {...(!isWeb && {
        pressStyle:
          isProductAvailable && !displayLockTag
            ? { opacity: 0.7, scale: 0.98 }
            : {},
        animation: 'quick',
      })}
    >
      <View
        onHoverIn={showIndicatorOnHover}
        testID="product-card"
        onHoverOut={handleHoverOut}
        cursor={isProductAvailable && !displayLockTag ? 'pointer' : 'default'}
        overflow="hidden"
        position="relative"
        shadowRadius={8}
        borderRadius={8}
        borderWidth={2}
        flex={1}
        borderColor="transparent"
        maxWidth={cardDimensions.width}
        minWidth={cardDimensions.width}
        shadowOffset={{ width: 0, height: 4 }}
        scale={1}
        hoverStyle={
          isProductAvailable
            ? {
                shadowColor: cardHoverShadow,
                shadowRadius: 48,
                shadowOffset: { width: 0, height: 16 },
                borderColor: '$border-hover',
                transition: 'all 0.7s ease',
              }
            : {}
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <View>
          <Animated.View
            style={{
              transform: [
                { scale: scaleValues[data.enrollmentId || data.id] || 1 },
              ],
            }}
          >
            <CoverImage
              uri={productLogo}
              height={cardDimensions.height}
              style={{
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottomLeftRadius: cardImageBorderRadius,
                borderBottomRightRadius: cardImageBorderRadius,
                filter: isProductAvailable ? 'unset' : 'grayscale(100%)',
                opacity: isNativeAndNotAvailable ? 0.2 : 1,
              }}
              alt={data.title}
            />
          </Animated.View>
          {displayBuyTag && (
            <ProductBadge
              variant="buy"
              badgeIconSize={badgeIconSize}
              label={t('myProducts.productCard.badges.buy')}
            />
          )}
          {shouldRenderLockBadge && (
            <ProductBadge
              variant="lock"
              badgeIconSize={badgeIconSize}
              label={formatDateShort(data.availableUntil || '')}
            />
          )}
          {displayLockTag && !displayBuyTag && !shouldRenderLockBadge && (
            <ProductBadge
              variant="lock"
              badgeIconSize={badgeIconSize}
              label={t('myProducts.productCard.badges.locked')}
            />
          )}
          {shouldRenderLiveBadge && (
            <ProductBadge
              variant="live"
              badgeIconSize={badgeIconSize}
              label={t('myProducts.productCard.badges.live_meet')}
              interpolatedOpacity={interpolatedOpacity}
            />
          )}
          {isHovered && (
            <LinearGradient
              colors={['transparent', 'rgba(0,0,0,0.9)']}
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                right: 0,
                height: 79,
              }}
            />
          )}
          {shouldRenderProgress && (
            <>
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.9)']}
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  right: 0,
                  height: 79,
                  borderBottomLeftRadius: cardImageBorderRadius,
                  borderBottomRightRadius: cardImageBorderRadius,
                }}
              />
              <ProductProgress {...progressProps} />
            </>
          )}
          {displayProgressOnHover && <ProductProgress {...progressProps} />}
        </View>
        {hasAuxiliarTitle && (
          <ProductCardContent
            testID="auxiliar-title"
            isContentAvailable={isProductAvailable}
            completedLessons={data.completedLessons}
            title={data.title}
            progress={data.progress}
            totalLessons={data.totalLessons || 0}
            isHorizontal={isHorizontal}
          />
        )}
      </View>
    </View>
  )
}
